import { Fragment, type ReactElement, type ReactNode, Suspense, useCallback, useRef, useState } from 'react';
import { ClickableIcon, HStack, IconArrowLeft, VStack } from '@direct-frontend/components';
import { Form } from '@adv-frontend/form-kit';
import { Drawer, DrawerHeader } from '@/shared/components/Drawer';
import { getRequestContext } from '@/shared/lib/requestContext';
import { ClientForm } from './components/ClientForm';
import { ClientFormPhoneVerification, ClientFormPhoneVerificationStub } from './components/ClientFormPhoneVerification';
import { clientForm, type ClientFormInput } from './model/clientForm';
import { CLIENT_FORM_ID } from './model/constants';
import type { ClientFormScreen, ClientPickerValue } from './types';
import styles from './ClientPicker.module.css';

interface ClientPickerPayload {
    onOpenModal: (formMode: ClientFormScreen) => void;
}

interface ClientPickerProps {
    value?: ClientPickerValue;
    onChange?: (value: ClientPickerValue) => void;
    children: (payload: ClientPickerPayload) => ReactElement;
    addonTop?: ReactNode;
    needPhoneVerification: boolean;
    ozonRegistrationButton: ReactNode;
}

export const ClientPicker = (props: ClientPickerProps) => {
    const { value, children, onChange: propOnChange, addonTop, needPhoneVerification, ozonRegistrationButton } = props;

    const [minHeight, setMinHeight] = useState<number>();
    const contentBlockRef = useRef<HTMLDivElement>(null);

    const { isDigitalOrder } = getRequestContext();
    const [formScreen, setFormScreen] = useState<ClientFormScreen>('form');

    const goNext = useCallback(() => {
        if (contentBlockRef.current) {
            setMinHeight(contentBlockRef.current.getBoundingClientRect().height);
        }

        setFormScreen('phone-verification');
    }, []);
    const goBack = useCallback(() => setFormScreen('form'), []);

    const [isOpen, setIsOpen] = useState(false);
    const onOpenModal = useCallback(
        (formScreen: ClientFormScreen) => {
            setFormScreen(formScreen);
            setIsOpen(true);
        },
        [setIsOpen]
    );
    const onCloseModal = useCallback(() => setIsOpen(false), [setIsOpen]);

    const onChange = useCallback(
        (values: ClientPickerValue) => {
            propOnChange?.(values);
        },
        [propOnChange]
    );

    const formTitle = isDigitalOrder ? 'Отправитель' : 'Получатель';
    const title =
        formScreen === 'form' ? (
            formTitle
        ) : (
            <HStack gap="4">
                <ClickableIcon color="current" onClick={goBack}>
                    <IconArrowLeft size="24" />
                </ClickableIcon>
                <span>Введите код подтверждения</span>
            </HStack>
        );

    const formInitialValue: ClientFormInput | undefined = value && {
        email: value.email,
        firstName: value.firstName,
        lastName: value.lastName,
        phone: value.phone,
        phoneVerified: needPhoneVerification ? '' : value.phone,
    };

    return (
        <Fragment>
            <Form
                id={CLIENT_FORM_ID}
                form={clientForm}
                initialValue={formInitialValue}
                context={{ needPhoneVerification }}
            >
                {/* snapPoints={[1]} - Оставляем один шаг раскрытия шторки, так как при нескольких шагах и динамической высоте шторки
                (а из-за наличия ошибок валидации высота шторки может изменяться),
                некорректно расчитывается смещение из-за чего появляются проблемы с отображением контента - SS-10889 */}
                <Drawer snapPoints={[1]} isOpen={isOpen} onClose={onCloseModal}>
                    <div className={styles.content} ref={contentBlockRef} style={{ minHeight }}>
                        <VStack>
                            {formScreen === 'form' && addonTop}

                            <DrawerHeader title={title} />
                        </VStack>

                        {formScreen === 'form' ? (
                            <ClientForm
                                goNext={goNext}
                                onClose={onCloseModal}
                                onSubmit={onChange}
                                ozonRegistrationButton={ozonRegistrationButton}
                            />
                        ) : (
                            <Suspense fallback={<ClientFormPhoneVerificationStub />}>
                                <ClientFormPhoneVerification
                                    goNext={goNext}
                                    onClose={onCloseModal}
                                    onSubmit={onChange}
                                />
                            </Suspense>
                        )}
                    </div>
                </Drawer>
            </Form>
            {children({ onOpenModal })}
        </Fragment>
    );
};
